import React, {useState} from "react";
import {FaListCheck, FaPen, FaPlus, FaTrash} from "react-icons/fa6";
import Button from "../../../../components/button";
import InputCheckbox from "../../../../components/input-checkbox";
import InputForm from "../../../../components/input-form";
import EditableTitle from "../editable-title";
import DetailSection from "./detail-section";

const CardChecklistsSection = ({card, onUpdate, onRemove, onEmpty, readOnly = false}) => {
    const [itemTitles, setItemTitles] = useState({});
    const [editingItem, setEditingItem] = useState(null);
    const legacyChecklist = card.checklist ?? [];
    const checklists = Array.isArray(card.checklists)
        ? card.checklists
        : (legacyChecklist.length > 0 ? [{id: `checklist-${card.id}`, title: "Checklist", items: legacyChecklist}] : []);

    const updateChecklists = (nextChecklists, historyText) => onUpdate({
        checklists: nextChecklists,
        checklist: nextChecklists.flatMap(checklist => checklist.items ?? []),
    }, historyText);

    const addChecklist = () => {
        const sequence = checklists.length + 1;
        updateChecklists([...checklists, {id: `checklist-${Date.now()}`, title: `Checklist ${sequence}`, items: []}]);
    };

    const updateChecklist = (checklistId, changes, historyText) => updateChecklists(
        checklists.map(checklist => checklist.id === checklistId ? {...checklist, ...changes} : checklist),
        historyText,
    );

    const removeChecklist = checklistId => {
        const target = checklists.find(checklist => checklist.id === checklistId);
        const nextChecklists = checklists.filter(checklist => checklist.id !== checklistId);
        updateChecklists(nextChecklists, `removeu o checklist “${target?.title || "Checklist"}”`);
        if (nextChecklists.length === 0) onEmpty();
    };

    const renameChecklist = (checklist, title) => updateChecklist(
        checklist.id,
        {title},
        `alterou o título do checklist de “${checklist.title || "Checklist"}” para “${title}”`,
    );

    const toggleItem = (checklist, item) => {
        const items = checklist.items ?? [];
        const nextCompleted = !item.completed;
        updateChecklist(
            checklist.id,
            {items: items.map(value => value.id === item.id ? {...value, completed: nextCompleted} : value)},
            `marcou o item “${item.title}” do checklist “${checklist.title || "Checklist"}” como ${nextCompleted ? "concluído" : "incompleto"}`,
        );
    };

    const removeItem = (checklist, itemId) => updateChecklist(
        checklist.id,
        {items: (checklist.items ?? []).filter(item => item.id !== itemId)},
        `removeu um item do checklist “${checklist.title || "Checklist"}”`,
    );

    const startEditingItem = (checklist, item) => setEditingItem({
        checklistId: checklist.id,
        itemId: item.id,
        title: item.title,
    });

    const saveEditingItem = (checklist, item) => {
        const title = String(editingItem?.title ?? "").trim();

        if (title && title !== item.title) {
            updateChecklist(
                checklist.id,
                {items: (checklist.items ?? []).map(value => value.id === item.id ? {...value, title} : value)},
                `alterou o item “${item.title}” do checklist “${checklist.title || "Checklist"}” para “${title}”`,
            );
        }

        setEditingItem(null);
    };

    const handleEditKeyDown = (event, checklist, item) => {
        if (event.key === "Enter") {
            event.preventDefault();
            saveEditingItem(checklist, item);
        }

        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            setEditingItem(null);
        }
    };

    const addItem = checklistId => {
        const title = itemTitles[checklistId]?.trim();
        if (!title) return;
        const target = checklists.find(checklist => checklist.id === checklistId);
        updateChecklist(checklistId, {items: [...(target?.items ?? []), {id: `check-${Date.now()}`, title, completed: false}]}, `adicionou um novo item ao checklist “${target?.title || "Checklist"}”`);
        setItemTitles(current => ({...current, [checklistId]: ""}));
    };

    const handleKeyDown = (event, checklistId) => {
        if (event.key === "Enter") {
            event.preventDefault();
            addItem(checklistId);
        }

        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            event.currentTarget.blur();
        }
    };

    return (
        <DetailSection icon={FaListCheck} title="Checklists" onRemove={onRemove} removeBtn={!readOnly} className={"mt-1"}>
            <div className="space-y-4">
                {checklists.map(checklist => {
                    const items = checklist.items ?? [];
                    const completedItems = items.filter(item => item.completed).length;
                    return (
                        <div key={checklist.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <div className="mb-3 flex items-center gap-2">
                                <EditableTitle value={checklist.title || "Checklist"}
                                               onSave={title => renameChecklist(checklist, title)}
                                               label="Título do checklist"
                                               className="min-w-0 flex-1 font-semibold text-slate-700"
                                               inputClassName="w-full"
                                               readOnly={readOnly}/>
                                {!readOnly &&
                                    <Button type="button" target="danger" onClick={() => removeChecklist(checklist.id)} col="!h-7 !px-2" title="Excluir checklist">
                                        <FaTrash/>
                                    </Button>
                                }
                            </div>
                            {items.length > 0 &&
                                <div className="mb-3 flex items-center gap-2">
                                    <span className="w-8 text-xs text-slate-500">
                                        {Math.round((completedItems / items.length) * 100)}%
                                    </span>
                                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
                                        <div className="h-full bg-emerald-500" style={{width: `${(completedItems / items.length) * 100}%`}} />
                                    </div>
                                </div>
                            }
                            <div className="space-y-2">
                                {items.map(item =>
                                    <div key={item.id} className="flex items-center gap-2 rounded-md bg-white p-2">
                                        {editingItem?.checklistId === checklist.id && editingItem?.itemId === item.id
                                            ? <InputForm id={`edit-checklist-${checklist.id}-${item.id}`}
                                                         label="Item" value={editingItem.title}
                                                         onChange={({target}) => setEditingItem(current => ({...current, title: target.value}))}
                                                         onBlur={() => saveEditingItem(checklist, item)}
                                                         onKeyDown={event => handleEditKeyDown(event, checklist, item)}
                                                         autoFocus col="flex-1" />
                                            : <InputCheckbox id={`checklist-${checklist.id}-${item.id}`}
                                                             label={item.title} checked={item.completed}
                                                             disabled={readOnly} onChange={() => toggleItem(checklist, item)}
                                                             col={`flex-1 ${item.completed ? "text-slate-400 line-through" : "text-slate-700"}`} />}
                                        {!readOnly &&
                                            <>
                                                {!(editingItem?.checklistId === checklist.id && editingItem?.itemId === item.id) &&
                                                    <Button type="button" target="primary" onClick={() => startEditingItem(checklist, item)} col="!h-7 !px-2" title="Editar item" aria-label={`Editar item ${item.title}`}>
                                                        <FaPen/>
                                                    </Button>
                                                }
                                                <Button type="button" target="danger" onClick={() => removeItem(checklist, item.id)} col="!h-7 !px-2" title="Excluir item" aria-label={`Excluir item ${item.title}`}>
                                                    <FaTrash/>
                                                </Button>
                                            </>}
                                    </div>
                                )}
                            </div>
                            {!readOnly &&
                                <div className="mt-3 flex items-center gap-2">
                                    <InputForm id={`checklist-item-${checklist.id}`}
                                               label="Item" value={itemTitles[checklist.id] ?? ""}
                                               onChange={({target}) => setItemTitles(current => ({...current, [checklist.id]: target.value}))}
                                               onKeyDown={event => handleKeyDown(event, checklist.id)}
                                               col="flex-1" />
                                    <Button type="button" target="primary" onClick={() => addItem(checklist.id)} title="Adicionar">
                                        <FaPlus/>
                                    </Button>
                                </div>
                            }
                        </div>
                    );
                })}
                {!readOnly && <Button type="button" target="primary" onClick={addChecklist}><FaPlus /> Adicionar</Button>}
            </div>
        </DetailSection>
    );
};

export default CardChecklistsSection;
